import React, { useState } from "react";
import { Col, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FiatCurrency } from "@nestcoinco/hyper-api-gateway-api-client";

import "./HowMuchWouldYouLikeToMake.scss";
import OnboardingHeader from "../../../components/Headers/OnboardingHeader/OnboardingHeader";
import {
  GOAL_ONBOARDING_STEPS,
  OnboardingSteps,
} from "../../../utils/constants";
import { formatCurrency } from "../../../utils/helpers";
import {
  getStoredSelectedCurrency,
  getStoredSelectedEarning,
  setStoredSelectedCurrency,
  setStoredSelectedEarnings,
} from "../../../utils/storage";
import { CustomSpinner } from "../../../components/CustomSpinner/CustomSpinner";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import useExchangeRatesQuery from "../../../react-query/queries/use-exchange-rates";
import CurrencySelectorDropdown from "../../../components/onboarding/CurrencySelectorDropdown/CurrencySelectorDropdown";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";
import HyperCoin from "../../../components/HyperCoin/HyperCoin";
import GoalTipBulbIcon from "../../../components/icons/GoalTipBulbIcon";

type HowMuchToMakeSelectionItemProps = {
  hyperAmount: string;
  amount: string;
  selected: string;
  onSelect: (value: string) => void;
};

function HowMuchToMakeSelectionItem({
  hyperAmount,
  amount,
  selected,
  onSelect,
}: HowMuchToMakeSelectionItemProps) {
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const isSelected = selected === hyperAmount;

  return (
    <button
      type="button"
      className={`m-0 how-much-to-make-item${isSelected ? " selected" : ""}`}
      onClick={() => onSelect(hyperAmount)}
    >
      <p className="hyper-amount-to-make">
        <HyperCoin className="hyper-coin" />
        <span className="hyper-amount">{formatCurrency(+hyperAmount)}</span>
        <span className="frequency">{t("daily")}</span>
      </p>
      <p className="fiat-amount">{amount}</p>
    </button>
  );
}

const HPR_EARNING_GOALS = [1000, 5000, 10000, 20000];

function HowMuchWouldYouLikeToMake() {
  const [selectedEarning, setSelectedEarning] = useState(
    () => getStoredSelectedEarning()?.toString() ?? "",
  );
  const [selectedCurrency, setSelectedCurrency] = useState<
    FiatCurrency | undefined
  >(() => getStoredSelectedCurrency() ?? undefined);
  const { goToStep } = useOnboardingSteps();
  const { getExchangeRate } = useExchangeRatesQuery();
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });

  const handleSelectAmount = (hyperAmount: string) => {
    if (!selectedCurrency) return;
    setSelectedEarning(hyperAmount);
    setStoredSelectedEarnings(+hyperAmount);
    setStoredSelectedCurrency(selectedCurrency);
    goToStep(OnboardingSteps.SET_STREAK_GOAL);
  };

  return (
    <OnboardingContainer className="MobileOnly position-relative d-flex flex-column gap-4 how-much-would-you-like-to-make-page">
      <Col className="how-much-would-you-like-to-make-header">
        <OnboardingHeader amountActive={1} steps={GOAL_ONBOARDING_STEPS} />
      </Col>

      <Stack gap={4} className="how-much-would-you-like-to-make-page-content">
        <h1 className="earnings-target-heading">
          {t("how_much_do_you_want_to_win_daily")}
        </h1>
        {!selectedCurrency && <CustomSpinner className="text-white" />}
        <Stack
          gap={2}
          className="how-much-would-you-like-to-make-selection-grid"
        >
          <CurrencySelectorDropdown
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
          />

          {selectedCurrency ? (
            <Stack
              gap={3}
              className="how-much-would-you-like-to-make-selection-options"
            >
              {HPR_EARNING_GOALS.map((goal) => (
                <HowMuchToMakeSelectionItem
                  key={goal}
                  hyperAmount={goal.toString()}
                  amount={formatCurrency(
                    goal * (getExchangeRate(selectedCurrency) ?? 0),
                    selectedCurrency,
                  )}
                  selected={selectedEarning}
                  onSelect={handleSelectAmount}
                />
              ))}
              <div className="hpr-definition-container">
                <GoalTipBulbIcon />
                <p className="hpr-definition">
                  HPR (<HyperCoin />) {t("hpr_coin_definition")}
                </p>
              </div>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </OnboardingContainer>
  );
}

export default HowMuchWouldYouLikeToMake;
